import { useQuery, useQueryClient } from 'react-query';
import { useState } from 'react';
import { 
  fetchPurchaseRecommendationData,
  patchPurchaseRecommendationData,
  fetchAvailableItens
} from "./services/PurchaseService";

import { ALLOWED_PURCHASE_STATUS } from '../components/components.constants';

export const usePurchaseRecommendation = (storeId, supplierId, options = {}) => {
  const queryClient = useQueryClient();
  const { 
    shouldFetchPurchaseRecommendationData = false,
    shouldFetchAvailableItens = false
  } = options;
  
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [isUpdateSuccess, setIsUpdateSuccess] = useState(false);

  const query = useQuery({
    queryKey: ['purchaseRecommendation', storeId],
    
    queryFn: async () => {

      if (!storeId) throw new Error('ID da loja é obrigatório');
      
      const apiData = await fetchPurchaseRecommendationData(storeId);

      const processedData = {
        ...apiData,
        selectedSubgroups: [...new Set(apiData.purchaseRecommendation.map(item => item.productSubgroupName).filter(Boolean))],
        selectedProducts: [...new Set(apiData.purchaseRecommendation.map(item => item.productName).filter(Boolean))],
        productIdsToUse: [...new Set(apiData.purchaseRecommendation.map(item => item.productId).filter(Boolean))],
        storeInformation: {
          region: apiData.purchaseRecommendation[0].storeInfo.store.region,
          name: apiData.purchaseRecommendation[0].storeInfo.store.name,
          city: apiData.purchaseRecommendation[0].storeInfo.store.city,
          address: apiData.purchaseRecommendation[0].storeInfo.store.address,
          cnpj:  apiData.purchaseRecommendation[0].storeInfo.store.cnpj
        }
      };      

      return processedData;
    },
    
    enabled: Boolean(storeId) && shouldFetchPurchaseRecommendationData,
    keepPreviousData: false,
    staleTime: 0,
    cacheTime: 0, 
    
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    
    onError: (error) => {
      console.error('Erro ao carregar dados de recomendação:', error);
      
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },

    ...options
  });

  const updateRecommendation = async (inputs, shouldRefetch = true) => {
    if (!inputs || Object.keys(inputs).length === 0) {
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);
    setIsUpdateSuccess(false);

    try {
      if (!storeId) throw new Error('ID da loja é obrigatório');

      const updatedData = await patchPurchaseRecommendationData(inputs, storeId);

      if (!updatedData) {
        setIsUpdateSuccess(true);
        return;
      }

      const isPartial = updatedData?.partial === true;
      const dataToUse = isPartial ? updatedData.updated : updatedData;

      queryClient.setQueryData(['purchaseRecommendation', storeId], (oldData) => {
        if (!oldData) return oldData;

        const updatedMap = new Map(
          dataToUse.map(item => [item.productId, item])
        );

        return {
          ...oldData,
          purchaseRecommendation: oldData.purchaseRecommendation
            .map(item => updatedMap.get(item.productId) ?? item)
            .filter(item => item.purchaseStatus !== ALLOWED_PURCHASE_STATUS.FINALIZED)
        };
      });

      if (shouldRefetch) await query.refetch();

      setIsUpdateSuccess(true);
      return updatedData;

    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }

      setUpdateError(error);
      throw error;

    } finally {
      setIsUpdating(false);
    }
  };

  const availableItensQuery = useQuery({
    queryKey: ['available-itens', storeId, supplierId],
    
    queryFn: async () => {
     
      const apiData = await fetchAvailableItens(storeId, supplierId); 
      return apiData;
    },
    
    enabled: Boolean(supplierId) && Boolean(storeId) && shouldFetchAvailableItens,
    keepPreviousData: false,
    staleTime: 0,
    cacheTime: 0, 
    
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    
    onError: (error) => {
      console.error('Erro ao carregar dados de recomendação:', error);
      
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },

    ...options
  });  

  const resetUpdate = () => {
    setUpdateError(null);
    setIsUpdateSuccess(false);
  };

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    updateRecommendation,
    isUpdating,
    updateError,
    isUpdateSuccess,
    resetUpdate,
    availableItens: {
      data: availableItensQuery.data,
      isLoading: availableItensQuery.isLoading
    }
  };
};